import React, { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useStudioTheme } from "../constants";

const AssistantAvatar = () => {
  const { palette } = useStudioTheme();

  return (
    <Box
      sx={{
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: "8px",
        backgroundColor: palette.brandTint,
        color: palette.brand,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mt: 0.25,
      }}
    >
      <TipsAndUpdatesRoundedIcon sx={{ fontSize: 15 }} />
    </Box>
  );
};

const TypingDots = () => {
  const { palette } = useStudioTheme();

  return (
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", py: 0.5 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            backgroundColor: palette.brandSoft,
            animation: "regoBlink 1.2s infinite ease-in-out both",
            animationDelay: `${i * 0.18}s`,
            "@keyframes regoBlink": {
              "0%, 80%, 100%": { opacity: 0.25, transform: "scale(0.8)" },
              "40%": { opacity: 1, transform: "scale(1)" },
            },
          }}
        />
      ))}
    </Box>
  );
};

const EmptyIntro = ({ fileTitle, suggestions = [], onPickSuggestion }) => {
  const { palette, fontStack, tc } = useStudioTheme();

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
      <Box sx={{ display: "flex", gap: 1.25, mb: 2.5 }}>
        <AssistantAvatar />
        <Box>
          <Typography
            sx={{
              fontFamily: fontStack,
              fontSize: "15px",
              fontWeight: 600,
              color: palette.ink,
              mb: 0.5,
              letterSpacing: "-0.01em",
            }}
          >
            Ready to refine{" "}
            {fileTitle ? (
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(90deg, ${palette.ink} 40%, ${palette.brand} 50%, ${palette.ink} 60%)`,
                  backgroundSize: "200% auto",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "studioShimmer 4s linear infinite",
                  "@keyframes studioShimmer": {
                    "0%": { backgroundPosition: "0% center" },
                    "100%": { backgroundPosition: "200% center" },
                  },
                }}
              >
                {fileTitle}
              </Box>
            ) : (
              "this document"
            )}
          </Typography>
          <Typography sx={{ fontFamily: fontStack, fontSize: "13.5px", color: palette.inkSoft, lineHeight: 1.55 }}>
            Pick a quick action or describe what to change.
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, pl: 5 }}>
        {suggestions.map((s) => {
          const label = typeof s === "string" ? s : s.label;
          return (
            <Box
              key={label}
              onClick={() => onPickSuggestion?.(s)}
              sx={{
                px: 1.5,
                py: 0.65,
                borderRadius: "999px",
                border: "none",
                backgroundColor: tc.status.info.bg,
                color: tc.status.info.text,
                cursor: "pointer",
                fontFamily: fontStack,
                fontSize: "12.5px",
                fontWeight: 600,
                transition: "all 0.15s ease",
                "&:hover": {
                  backgroundColor: palette.brandTint,
                  transform: "translateY(-1px)",
                },
              }}
            >
              {label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const ChatPanel = ({
  messages,
  loading,
  fileTitle,
  suggestions,
  onPickSuggestion,
}) => {
  const endRef = useRef(null);
  const { palette, fontStack } = useStudioTheme();

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const isEmpty = !messages || messages.length === 0;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: 0,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        px: 3,
        py: 2.5,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {isEmpty && !loading && (
        <EmptyIntro
          fileTitle={fileTitle}
          suggestions={suggestions}
          onPickSuggestion={onPickSuggestion}
        />
      )}

      {messages.map((m, i) =>
        m.role === "user" ? (
          <Box
            key={i}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              opacity: 0,
              animation: "chatFadeSlideIn 0.28s ease forwards",
              animationDelay: `${i * 0.06}s`,
              "@keyframes chatFadeSlideIn": {
                from: { opacity: 0, transform: "translateY(6px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            <Box
              sx={{
                maxWidth: "82%",
                backgroundColor: palette.buttonPrimary,
                color: palette.buttonText,
                px: 1.5,
                py: 1,
                borderRadius: "14px",
                borderBottomRightRadius: "4px",
                fontFamily: fontStack,
                fontSize: "14px",
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
              }}
            >
              {m.content}
            </Box>
          </Box>
        ) : (
          <Box
            key={i}
            sx={{
              display: "flex",
              gap: 1,
              opacity: 0,
              animation: "chatFadeSlideIn 0.28s ease forwards",
              animationDelay: `${i * 0.06 + 0.04}s`,
              "@keyframes chatFadeSlideIn": {
                from: { opacity: 0, transform: "translateY(6px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            <AssistantAvatar />
            <Box
              sx={{
                maxWidth: "82%",
                px: 1.5,
                py: 1.1,
                borderRadius: "14px",
                borderTopLeftRadius: "4px",
                backgroundColor: palette.surface,
                border: `1px solid ${palette.border}`,
                fontFamily: fontStack,
                fontSize: "14px",
                lineHeight: 1.6,
                color: palette.ink,
                "& p": { m: 0, mb: 0.75, "&:last-child": { mb: 0 } },
                "& strong": { color: palette.brandDeep, fontWeight: 600 },
                "& ul": { m: 0, pl: 2.25, mb: 0.5 },
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
            </Box>
          </Box>
        )
      )}

      {loading && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <AssistantAvatar />
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: "14px",
              borderTopLeftRadius: "4px",
              backgroundColor: palette.surface,
              border: `1px solid ${palette.border}`,
            }}
          >
            <Typography
              sx={{
                fontFamily: fontStack,
                fontSize: "12.5px",
                color: palette.inkFaint,
                mb: 0.5,
              }}
            >
              Rego AI is drafting…
            </Typography>
            <TypingDots />
          </Box>
        </Box>
      )}

      <div ref={endRef} />
    </Box>
  );
};

export default ChatPanel;
